import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  organizationsAPI, 
  categoriesAPI, 
  subjectsAPI, 
  classroomsAPI, 
  periodsAPI 
} from '../../api';
import { useTenant } from '../../contexts/TenantContext';
// Removed unused useSchoolTimezone import
import CategorySidebar from './CategorySidebar';
import ClassDetail from "./ClassDetail";
import ManageClassesModal from "./ManageClasses";

// Master list of classes
const ALL_CLASSES = [
  {
    id: 1,
    name: "MAT-101",
    instructor: {
      name: "Micheal Williams",
      email: "michaelwilliams@simmple.com",
      image: "https://randomuser.me/api/portraits/men/10.jpg"
    },
    periods: [
      {
        name: "Period 1",
        reports: [
          { id: 1, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" },
          { id: 2, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" },
          { id: 3, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" },
          { id: 4, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" },
          { id: 5, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" },
          { id: 6, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" },
          { id: 7, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" }
        ],
        students: [
          { name: "Esthera Jackson", email: "esthera@simmple.com", role: "Teacher", subject: "CST-104", school: "City Tech", avatar: "https://randomuser.me/api/portraits/women/44.jpg" },
          { name: "Alexa Liras",     email: "alexa@simmple.com",    role: "Teacher", subject: "ENG-4010", school: "City Tech", avatar: "https://randomuser.me/api/portraits/women/47.jpg" },
          { name: "Laurent Michael", email: "laurent@simmple.com",  role: "Substitute", subject: "Temporary Staff", school: "Hunter", avatar: "https://randomuser.me/api/portraits/men/32.jpg" },
          { name: "Freduardo Hill",  email: "freduadro@simmple.com", role: "Teacher", subject: "SCI-202", school: "Hunter", avatar: "https://randomuser.me/api/portraits/men/44.jpg" },
          { name: "Daniel Thomas",   email: "daniel@simmple.com",   role: "Program Manager", subject: "Robotics Club", school: "City Tech", avatar: "https://randomuser.me/api/portraits/men/45.jpg" },
          { name: "Mark Wilson",     email: "mark@simmple.com",      role: "Teacher", subject: "MAT-206", school: "Hunter", avatar: "https://randomuser.me/api/portraits/men/46.jpg" }
        ]
      },
      {
        name: "Period 2",
        reports: [
          { id: 8, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" },
          { id: 9, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" },
          { id: 10, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" }
        ],
        students: [
          { name: "Esthera Jackson", email: "esthera@simmple.com", role: "Teacher", subject: "CST-104", school: "City Tech", avatar: "https://randomuser.me/api/portraits/women/44.jpg" },
          { name: "Alexa Liras",     email: "alexa@simmple.com",    role: "Teacher", subject: "ENG-4010", school: "City Tech", avatar: "https://randomuser.me/api/portraits/women/47.jpg" },
          { name: "Laurent Michael", email: "laurent@simmple.com",  role: "Substitute", subject: "Temporary Staff", school: "Hunter", avatar: "https://randomuser.me/api/portraits/men/32.jpg" },
          { name: "Daniel Thomas",   email: "daniel@simmple.com",   role: "Program Manager", subject: "Robotics Club", school: "City Tech", avatar: "https://randomuser.me/api/portraits/men/45.jpg" }
        ]
      },
      {
        name: "Period 3",
        reports: [ { id: 11, instructor: "Michael Williams", timestamp: "Dec 19 2025, 7:20 PM" } ],
        students: [
          { name: "Esthera Jackson", email: "esthera@simmple.com", role: "Teacher", subject: "CST-104", school: "City Tech", avatar: "https://randomuser.me/api/portraits/women/44.jpg" },
          { name: "Alexa Liras",     email: "alexa@simmple.com",    role: "Teacher", subject: "ENG-4010", school: "City Tech", avatar: "https://randomuser.me/api/portraits/women/47.jpg" },
          { name: "Laurent Michael", email: "laurent@simmple.com",  role: "Substitute", subject: "Temporary Staff", school: "Hunter", avatar: "https://randomuser.me/api/portraits/men/32.jpg" }
        ]
      }
    ]
  },
  
  {
    id: 2,
    name: "MAT-206",
    instructor: {
      name: "Prof. Calculus",
      email: "calc@citytech.edu",
      image: "https://randomuser.me/api/portraits/women/32.jpg"
    },
    periods: [
      {
        name: "Period 1",
        reports: [ { id: 1, instructor: "Prof. Calculus", timestamp: "Feb 15 2025, 11:30 AM" } ],
        students: [
          { name: "Bob Integral", email: "bob@calc.edu", role: "Student", subject: "MAT-206", school: "City Tech", avatar: "https://randomuser.me/api/portraits/men/82.jpg" }
        ]
      }
    ]
  }
];

// Removed unused SCHOOL_CATEGORIES stub

// Default category structure for new schools
// Removed unused DEFAULT_CATEGORIES stub

export default function ClassManagementPage() {
  const { getAccessTokenSilently } = useAuth0();
  const { organization, currentSchool } = useTenant();
  const location = useLocation();
  
  // Get classId from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const selectedClassId = searchParams.get('classId');
  
  // Single consolidated state object to prevent interdependent re-renders
  const [appState, setAppState] = useState({
    // Loading states
    loading: true,
    error: null,
    
    // Core data
    allClasses: [],
    activeClass: null,
    
    // Organization and hierarchy data
    organization_id: organization?.id || null,
    schools: [],
    categories: [],
    subjects: [],
    classrooms: [],
    
    // Current selections
    selectedSchool: "",
    selectedSchoolId: null,
    
    // Derived/computed data (calculated from other state)
    categoriesTree: {},
    availableSchools: []
  });

  // // Modal states (separate since they don't affect data loading)
   const navigate = useNavigate();
  // const location = useLocation();
  
  // Core data
  const [allClasses] = useState(ALL_CLASSES);
  const [activeClass, setActiveClass] = useState(allClasses[0]);

  // Organization and database data
  // Local demo state only

  // Schools from API - now tracking both name and ID
  // const [availableSchools] = useState([]);

  // Sidebar categories/state - now built from database
  // const [selectedSchool] = useState("");
  // const [selectedSchoolId] = useState(null);

  // Modal open/close with data refresh
  const [showManageClasses, setShowManageClasses] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  
  const openManageClasses = () => setShowManageClasses(true);
  const closeManageClasses = () => {
    setShowManageClasses(false);
    // Refresh data after closing modal (in case changes were made)
    loadAllData();
  };

  // Build categories structure from database data
  const buildCategoriesFromData = (categoriesData, subjectsData, classroomsData, schoolName) => {
    const newCategories = {};
    
    // Process all categories (backend already filtered appropriately)
    categoriesData.forEach(category => {
      const categoryName = category.name;
      newCategories[categoryName] = {};
      
      // Find subjects for this category
      const categorySubjects = subjectsData.filter(subject => 
        subject.category_id === category.id
      );
      
      categorySubjects.forEach(subject => {
        const subjectCode = subject.code || subject.name;
        
        // Find classrooms for this subject
        const subjectClassrooms = classroomsData.filter(classroom => 
          classroom.subject_id === subject.id
        );
        
        // Sort classrooms: regular classes first, then joint classes, then alphabetical
        const sortedClassrooms = subjectClassrooms.sort((a, b) => {
          // Regular classes first
          if (!a.is_joint_class && b.is_joint_class) return -1;
          if (a.is_joint_class && !b.is_joint_class) return 1;
          // Then sort alphabetically by name
          return a.name.localeCompare(b.name);
        });
        
        // Store full classroom objects instead of just names
        newCategories[categoryName][subjectCode] = sortedClassrooms;
      });
    });
    
    return newCategories;
  };

  // Helper function to convert 24-hour time to 12-hour format
  const convertTo12HourFormat = (time24) => {
    if (!time24) return '8:00AM';
    
    // Handle different time formats
    let timeStr = time24;
    if (typeof timeStr === 'string' && timeStr.includes(':')) {
      // Extract just the time part if it includes seconds (e.g., "10:00:00" -> "10:00")
      const timeParts = timeStr.split(':');
      if (timeParts.length >= 2) {
        const hours = parseInt(timeParts[0]);
        const minutes = timeParts[1];
        
        if (hours === 0) {
          return `12:${minutes}AM`;
        } else if (hours < 12) {
          return `${hours}:${minutes}AM`;
        } else if (hours === 12) {
          return `12:${minutes}PM`;
        } else {
          return `${hours - 12}:${minutes}PM`;
        }
      }
    }
    
    // If format is already 12-hour or unknown, return as-is
    return timeStr;
  };

  // Single comprehensive data loading function
  const loadAllData = useCallback(async () => {
    try {
      setAppState(prev => ({ ...prev, loading: true, error: null }));
      const token = await getAccessTokenSilently();
      
      // Get the current school ID for filtering
      const currentSchoolId = currentSchool?.unique_id || currentSchool?.unique_id;
      
      // If no current school is selected, don't load any data
      if (!currentSchool || !currentSchoolId) {
        setAppState(prev => ({
          ...prev,
          loading: false,
          error: null,
          categoriesTree: {},
          allClasses: []
        }));
        setActiveClass(null);
        return;
      }
      
      // Step 1: Get organization (for fallback - backend should get this from JWT)
      const orgData = await organizationsAPI.getMyOrganization(token);
      const organization = Array.isArray(orgData) ? orgData[0] : orgData;
      
      // Step 2: Pass school_id to get school-specific data
      const [categoriesData, subjectsData, classroomsData] = await Promise.all([
        categoriesAPI.getAllCategories(token, currentSchoolId), // Pass school parameter
        subjectsAPI.getAllSubjects(token, null, currentSchoolId),     // Pass school parameter (categoryId=null)  
        classroomsAPI.getAllClassrooms(token, null, currentSchoolId)  // Pass school parameter (subjectId=null)
      ]);
      
      
      // Step 3: Build categories tree from backend-filtered data
      const categoriesTree = buildCategoriesFromData(
        categoriesData, 
        subjectsData, 
        classroomsData, 
        currentSchool?.name || "Current School"
      );
      
      // Step 4: Auto-select first appropriate classroom (backend already filtered data)
      let newActiveClass = null;
      
      if (classroomsData.length > 0) {
        // Since joint classes are no longer shared across schools,
        // all returned classrooms should be appropriate for auto-selection
        const classroomToSelect = classroomsData[0];
        
        if (classroomToSelect) {
          // Load periods for all classrooms in parallel
          const periodsPromises = classroomsData.map(classroom => 
            periodsAPI.getPeriodsByClassroom(classroom.id, token).catch(error => {
              console.warn(`Failed to load periods for classroom ${classroom.id}:`, error);
              return []; // Return empty array on failure
            })
          );
          
          const allPeriodsData = await Promise.all(periodsPromises);
          
          
          // Build class objects with real period data for the selected classroom
          const selectedClassroom = classroomToSelect;
          // Find the index of this classroom in the full classroomsData array to get the right periods
          const selectedClassroomIndex = classroomsData.findIndex(c => c.id === selectedClassroom.id);
          const selectedClassroomPeriods = allPeriodsData[selectedClassroomIndex] || [];
          
          newActiveClass = {
            id: selectedClassroom.id,
            name: selectedClassroom.name,
            description: selectedClassroom.description || '',
            school_id: selectedClassroom.school_id,
            organization_id: selectedClassroom.organization_id,
            instructor: {
              name: selectedClassroom.synced_teacher?.name || "Unassigned",
              email: selectedClassroom.synced_teacher?.email || "",
              image: selectedClassroom.synced_teacher?.avatar || "https://randomuser.me/api/portraits/men/10.jpg"
            },
            // Use real periods from API
            periods: selectedClassroomPeriods.length > 0 
              ? selectedClassroomPeriods.map(period => ({
                  id: period.id,
                  name: period.name || `Period ${period.period_number || 1}`,
                  startTime: convertTo12HourFormat(period.start_time),
                  endTime: convertTo12HourFormat(period.end_time),
                  start_time: period.start_time, // Keep raw format for compatibility
                  end_time: period.end_time,     // Keep raw format for compatibility
                  dayOfWeek: period.day_of_week,
                  periodNumber: period.period_number,
                  synced_teacher: period.synced_teacher, // Include instructor assignment
                  reports: [], // TODO: Load from attendance API
                  students: [], // TODO: Load from enrollment API
                  _dbPeriod: period // Store original database object
                }))
              : [], // No periods yet - can be added via "Add Period" button
            _dbClassroom: selectedClassroom
          };
        } else {
          // No classrooms available - show empty state
          newActiveClass = null;
        }
      } else {
        // No classrooms at all - show empty state
        newActiveClass = null;
      }
      
      // Step 5: Update all state at once
      setAppState(prev => ({
        ...prev,
        loading: false,
        error: null,
        organization_id: organization.id,
        categories: categoriesData,
        subjects: subjectsData,
        classrooms: classroomsData,
        selectedSchool: currentSchool?.name || "",
        selectedSchoolId: currentSchool?.id || null,
        categoriesTree: categoriesTree,
        // Remove schools and availableSchools - no longer needed
        schools: [],
        availableSchools: [],
        // Set activeClass (could be null for schools with no classes)
        activeClass: newActiveClass
      }));
      
    } catch (error) {
      console.error('Error loading all data:', error);
      setAppState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load class data',
        categoriesTree: {} // Empty fallback instead of dummy data
      }));
    }
  }, [getAccessTokenSilently, currentSchool]);

  // Simple useEffect pattern - only load data on mount
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Watch for school changes from TenantContext
  useEffect(() => {
    if (currentSchool) {
      loadAllData(); // Reload data for the new school
    }
  }, [currentSchool, loadAllData]);

  // Function to handle class selection (moved from removed handleSelectClass)
  const handleSelectClassFromSidebar = useCallback(async (classroomObject) => {
    // Handle database classroom object with period loading
    if (classroomObject && classroomObject.id) {
      try {
        const token = await getAccessTokenSilently();
        const periodsData = await periodsAPI.getPeriodsByClassroom(classroomObject.id, token);
        
        // Transform database classroom object to match expected ClassDetail format
        const transformedClass = {
          id: classroomObject.id,
          name: classroomObject.name,
          description: classroomObject.description || '',
          school_id: classroomObject.school_id,
          organization_id: classroomObject.organization_id,
          instructor: {
            name: classroomObject.synced_teacher?.name || "Unassigned",
            email: classroomObject.synced_teacher?.email || "",
            image: classroomObject.synced_teacher?.avatar || "https://randomuser.me/api/portraits/men/10.jpg"
          },
          // Use real periods from API
          periods: periodsData.length > 0 
            ? periodsData.map(period => ({
                id: period.id,
                name: period.name || `Period ${period.period_number || 1}`,
                startTime: convertTo12HourFormat(period.start_time),
                endTime: convertTo12HourFormat(period.end_time),
                start_time: period.start_time, // Keep raw format for compatibility
                end_time: period.end_time,     // Keep raw format for compatibility
                dayOfWeek: period.day_of_week,
                periodNumber: period.period_number,
                synced_teacher: period.synced_teacher, // Include instructor assignment
                reports: [], // TODO: Load from attendance API
                students: [], // TODO: Load from enrollment API
                _dbPeriod: period // Store original database object
              }))
            : [], // No periods yet - can be added via "Add Period" button
          _dbClassroom: classroomObject
        };
        
        setAppState(prev => ({ ...prev, activeClass: transformedClass }));
      } catch (error) {
        console.error('Error loading periods for classroom:', error);
        // Fallback: create class without periods
        const fallbackClass = {
          id: classroomObject.id,
          name: classroomObject.name,
          description: classroomObject.description || '',
          school_id: classroomObject.school_id,
          organization_id: classroomObject.organization_id,
          instructor: {
            name: classroomObject.synced_teacher?.name || "Unassigned",
            email: classroomObject.synced_teacher?.email || "",
            image: classroomObject.synced_teacher?.avatar || "https://randomuser.me/api/portraits/men/10.jpg"
          },
          periods: [], // Empty periods - user can add via "Add Period"
          _dbClassroom: classroomObject
        };
        setAppState(prev => ({ ...prev, activeClass: fallbackClass }));
      }
    }
  }, [getAccessTokenSilently]);

  // Handle class selection from URL parameters
  useEffect(() => {
    if (selectedClassId && appState.allClasses.length > 0) {
      // Find the classroom by ID
      const foundClassroom = appState.allClasses.find(c => c.id.toString() === selectedClassId);
      if (foundClassroom && foundClassroom.id !== appState.activeClass?.id) {
        // Auto-select the class from URL parameter
        handleSelectClassFromSidebar(foundClassroom);
      }
    }
  }, [selectedClassId, appState.allClasses, appState.activeClass, handleSelectClassFromSidebar]);

  // Handle school selection from dropdown
  // Inject a new student into the active class's current period
  const handleAddStudent = useCallback((student) => {
    setAppState(prev => ({
      ...prev,
      allClasses: prev.allClasses.map(c =>
        c.id === prev.activeClass.id
          ? {
              ...c,
              periods: c.periods.map(p =>
                p.name === prev.activeClass.periods.find(cp => cp.name === p.name)?.name
                  ? { ...p, students: [...p.students, student] }
                  : p
              )
            }
          : c
      )
    }));
  }, []);

  // Handle updating an entire class (including periods)
  const handleUpdateClass = useCallback(async (classroomIdOrUpdatedClass) => {
    try {
      // If it's an object, use the old behavior for backward compatibility
      if (typeof classroomIdOrUpdatedClass === 'object') {
        const updatedClass = classroomIdOrUpdatedClass;
        setAppState(prev => ({
          ...prev,
          allClasses: prev.allClasses.map(c => c.id === updatedClass.id ? updatedClass : c),
          activeClass: updatedClass
        }));
        return;
      }
      
      // If it's an ID, fetch the updated classroom data
      const classroomId = classroomIdOrUpdatedClass;
      
      const token = await getAccessTokenSilently();
      const updatedClassroom = await classroomsAPI.getClassroomById(classroomId, token);
      
      // Structure the data to match the expected format with _dbClassroom
      const formattedClass = {
        id: updatedClassroom.id,
        name: updatedClassroom.name,
        _dbClassroom: updatedClassroom,
        // Map periods to the expected format - CONSISTENT with initial load format
        periods: updatedClassroom.periods?.map(period => ({
          id: period.id,
          name: period.name,
          startTime: convertTo12HourFormat(period.start_time),
          endTime: convertTo12HourFormat(period.end_time),
          start_time: period.start_time, // Keep raw format for API compatibility
          end_time: period.end_time,     // Keep raw format for API compatibility
          day_of_week: period.day_of_week,
          synced_teacher: period.synced_teacher,
          _dbPeriod: period,
          students: [], // Will be loaded separately
          reports: []   // Will be loaded separately
        })) || []
      };
      
      
      setAppState(prev => {
        const newState = {
          ...prev,
          allClasses: prev.allClasses.map(c => c.id === updatedClassroom.id ? formattedClass : c),
          activeClass: prev.activeClass?._dbClassroom?.id === updatedClassroom.id ? formattedClass : prev.activeClass
        };
        
        return newState;
      });
      
    } catch (error) {
      console.error('❌ Error updating class:', error);
      throw error;
    }
  }, [getAccessTokenSilently]);

  // Period management handlers
  const handleCreatePeriod = useCallback(async (classroomId, periodData) => {
    try {
      const token = await getAccessTokenSilently();
      await periodsAPI.createPeriod(periodData, token);
      // Reload data to refresh the periods
      await loadAllData();
    } catch (error) {
      console.error('Error creating period:', error);
      throw error;
    }
  }, [getAccessTokenSilently, loadAllData]);

  const handleUpdatePeriod = useCallback(async (periodId, periodData) => {
    try {
      const token = await getAccessTokenSilently();
      await periodsAPI.updatePeriod(periodId, periodData, token);
      // Reload data to refresh the periods
      await loadAllData();
    } catch (error) {
      console.error('Error updating period:', error);
      throw error;
    }
  }, [getAccessTokenSilently, loadAllData]);

  const handleDeletePeriod = useCallback(async (periodId) => {
    try {
      const token = await getAccessTokenSilently();
      await periodsAPI.deletePeriod(periodId, token);
      // Reload data to refresh the periods
      await loadAllData();
    } catch (error) {
      console.error('Error deleting period:', error);
      throw error;
    }
  }, [getAccessTokenSilently, loadAllData]);

  // Handle creating the category from the overlay
  const handleCreateCategory = async () => {
    if (!newCategoryName?.trim()) {
      alert('Please enter a category name');
      return;
    }
    
    if (!appState.organization_id) {
      alert('Organization not loaded. Please try again.');
      return;
    }
    
    if (!appState.selectedSchoolId) {
      alert('No school selected. Please select a school first.');
      return;
    }
    
    try {
      const token = await getAccessTokenSilently();
      const categoryData = {
        name: newCategoryName.trim(),
        school_id: appState.selectedSchoolId
        // Let the backend calculate the correct order_index
        // organization_id is now handled by JWT middleware
      };
      
      await categoriesAPI.createCategory(categoryData, token);
      // Reload data to include the new category
      await loadAllData();
      // Close the overlay and reset form
      setShowAddCategory(false);
      setNewCategoryName("");
    } catch (error) {
      console.error('Error adding category:', error);
      // Show more detailed error message
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert('Failed to add category: ' + errorMessage);
    }
  };

  // Handle canceling category creation
  const handleCancelAddCategory = () => {
    setShowAddCategory(false);
    setNewCategoryName("");
  };

  // Stub attendance
  const handleTakeAttendance = () => 
  {
    if (activeClass){
      navigate(`/attendance/${activeClass.id}`, { state: { className: activeClass.name } });
    }
  };

  useEffect(() => {
  if (location.state?.updatedClassId){
    const updatedId = location.state.updatedClassId;
    if (activeClass?.id === Number(updatedId)) {
      // Refresh the active class data
      loadAllData();
    }
  }
}, [location.state?.updatedClassId, activeClass, loadAllData]);

  return (
    <>
      {/* Main Layout with Sidebar */}
      <div className="flex gap-4 p-1">
        {/* Left Sidebar - Categories */}
        <div className="w-72 flex-shrink-0">
          <CategorySidebar
            categories={appState.categoriesTree}
            onSelectClass={handleSelectClassFromSidebar}
            onManage={openManageClasses}
            onAddCategory={() => setShowAddCategory(true)}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 space-y-4">
        {appState.loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-gray-600">Loading class data...</div>
          </div>
        ) : appState.error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-red-600 text-lg mb-2">Error loading data</div>
              <div className="text-gray-600 mb-4">{appState.error}</div>
              <button 
                onClick={() => loadAllData()}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Class Details */}
            {appState.activeClass ? (
              <ClassDetail
                key={`${appState.activeClass.id}-${appState.activeClass._dbClassroom?.school_id || appState.activeClass.school_id || 'no-school'}`}
                activeClass={appState.activeClass}
                onAddStudent={handleAddStudent}
                onTakeAttendance={handleTakeAttendance}
                onUpdateClass={handleUpdateClass}
                onCreatePeriod={handleCreatePeriod}
                onUpdatePeriod={handleUpdatePeriod}
                onDeletePeriod={handleDeletePeriod}
              />
            ) : (
              <div className="bg-white rounded-2xl border border-dashed border-2 border-gray-200 p-6 flex flex-col h-[800px]">
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-md">
                    <div className="text-gray-600 text-lg mb-2 font-semibold">
                      No Classes Assigned
                    </div>
                    <div className="text-gray-500 text-sm mb-6">
                      You don't have any classes assigned to you yet.
                    </div>
                    <button
                      onClick={openManageClasses}
                      className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow hover:bg-blue-700 transition mb-4"
                    >
                      Create a New Class
                    </button>
                    <div className="text-gray-500 text-sm">
                      Or ask your administrator to assign you to an existing class.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        </div> {/* End Main Content Area */}
      </div> {/* End Main Layout with Sidebar */}

      {/* Manage Classes Modal */}
      {showManageClasses && (
        <ManageClassesModal
          onClose={closeManageClasses}
          selectedSchoolId={appState.selectedSchoolId}
          selectedSchoolName={appState.selectedSchool}
          organization_id={appState.organization_id}
          selectedSchool={currentSchool} // Use currentSchool from TenantContext
        />
      )}

      {/* Add Category Modal */}
      {showAddCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Add New Category</h2>
            <p className="text-gray-600 mb-4">
              Enter a name for the new category (e.g., "Core Subjects", "Electives", "After School Programs")
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category Name
              </label>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Enter category name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateCategory();
                  }
                  if (e.key === 'Escape') {
                    handleCancelAddCategory();
                  }
                }}
              />
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelAddCategory}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCategory}
                disabled={!newCategoryName.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              >
                Create Category
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
